/**
 * Catalog toolbar bridge — collection + search.
 *
 * Two jobs, both born from the same constraint: on the search template the Globo Smart Filter app
 * re-renders the product grid, and its own sort/limit strip is hidden by theme CSS. So:
 *
 *  1. SORT PROXY. `snippets/collection-page-toolbar.liquid` renders the sort control under
 *     `data-toolbar-sorting-proxy` instead of `data-toolbar-sorting` on that template, which makes
 *     `storefont-filters.js` skip it (its section-level replaceWith would destroy the app's markup).
 *     This file drives the control instead: it reconciles the theme's Shopify sort options against
 *     the sorts the app actually offers, then forwards a pick to the app. If the app is absent it
 *     falls back to the theme's own native sort path, so the control is never a dead button.
 *
 *  2. RESET PILL. Liquid can only see Shopify facets and tags. App-owned params (vehicle pickers,
 *     app filters) are invisible server-side, so the pill is revealed from the URL here and its href
 *     is rebuilt to drop every filter param while preserving the search term and the chosen sort.
 *
 * Plus: the app replaces the grid's children, which strips the state the theme applies to product
 * cards. A childList observer re-applies the column view and the wishlist/compare button states,
 * exactly as `Collection.init()` would after a theme-side render.
 */
(() => {
  if (window.__mexCatalogToolbar) return;
  window.__mexCatalogToolbar = true;

  const PROXY = "[data-toolbar-sorting-proxy]";
  const APP_SORT_MENU = "#globo-dropdown-sort_options";
  const APP_SORT_TRIGGER = "#sortButton";
  // The app-owned grid on the search template, and the theme's own on the collection template.
  // Both need watching: the app can take the collection grid over too, and when it does the
  // empty state has to react there as well.
  const GRID = "[data-globo-filter-items]";
  const GRID_FALLBACK = "[data-product-container]";
  const APP_SUMMARY = ".gf-summary";
  const MIRRORED = "mex-count-mirrored";

  // Measured on this store, the filter app names its sorts with Shopify's own values
  // (relevance, best-selling, price-ascending, …) plus app-only extras such as sale-descending.
  // So resolution is identity-first, with aliases only for the alternative spellings other builds
  // of the app are known to emit — never a fixed table, which would silently rot.
  const SORT_ALIASES = {
    "price-ascending": ["price-low-to-high", "price-asc"],
    "price-descending": ["price-high-to-low", "price-desc"],
    "created-ascending": ["date-old-to-new"],
    "created-descending": ["date-new-to-old"],
    "title-ascending": ["alpha-asc", "a-z"],
    "title-descending": ["alpha-desc", "z-a"],
  };

  /** Resolve a Shopify sort_by value to whatever this app build calls it, or null. */
  const toAppSort = (shopifyValue, available) => {
    if (!shopifyValue) return null;
    if (available.has(shopifyValue)) return shopifyValue;
    for (const alias of SORT_ALIASES[shopifyValue] || []) if (available.has(alias)) return alias;
    return null;
  };

  /** Inverse: an app sort back to a Shopify sort_by value, for the no-app fallback path. */
  const toShopifySort = (appSort) => {
    if (!appSort) return "";
    for (const [shopify, aliases] of Object.entries(SORT_ALIASES)) {
      if (appSort === shopify || aliases.includes(appSort)) return shopify;
    }
    // Identity when the app uses Shopify's own vocabulary; app-only sorts have no equivalent.
    return /^(relevance|best-selling|manual|price-|created-|title-)/.test(appSort) ? appSort : "";
  };

  // Query params that describe the SEARCH or the VIEW, not a filter. Everything else is what Reset
  // drops. A keep-list rather than a block-list: the app renaming a param must not silently break
  // Reset.
  //
  // `sort_by` is kept deliberately. It is a sibling control, not a filter: clearing filters must
  // leave the shopper's chosen sort alone, and a sort on its own must not make the pill appear.
  // Omitting it was a defect — the Products menu item links to /collections/all?sort_by=most-relevant,
  // so every arrival from the main nav counted one dropped param and revealed a pill whose only
  // effect was to strip that sort. Same reasoning as the has_applied gate in
  // snippets/collection-page-toolbar.liquid.
  const KEEP_PARAMS = new Set([
    "q",
    "sort_by",
    "options[prefix]",
    "options[unavailable_products]",
    "options[fields]",
    "type",
    "locale",
    "currency",
    "view",
  ]);

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /** The product grid on whichever template we are on — app-owned first, theme's own second. */
  const gridNode = () => $(GRID) || $(GRID_FALLBACK);

  const appSortMap = () =>
    new Map($$(`${APP_SORT_MENU} li[data-sort]`).map((li) => [li.dataset.sort, li.textContent.trim()]));

  /* ── sort proxy ─────────────────────────────────────────────────────────── */

  /**
   * Reconcile the theme's Shopify sort list against what the app can actually do:
   * drop theme options the app has no equivalent for (they would be dead controls), and adopt
   * app-only options using the app's own label. Read at runtime so re-configuring the app in its
   * admin is picked up without a code change.
   */
  const reconcileSortOptions = (wrap) => {
    const available = appSortMap();
    if (!available.size) return; // app not present — leave the theme's list untouched

    const native = $("select.js-selectNative", wrap);
    const fakeList = $(".m-select-custom--options", wrap);
    const mobileList = $(".m-sortby-mobile--list");
    if (!native || !fakeList) return;

    const keep = [];
    [...native.options].forEach((opt) => {
      const appSort = toAppSort(opt.value, available);
      if (appSort) {
        keep.push({ value: opt.value, appSort, label: opt.text.trim(), selected: opt.selected });
        available.delete(appSort);
      }
    });
    // App-only sorts (e.g. a "% Sale off") the theme's Shopify list has no value for.
    available.forEach((label, appSort) => {
      keep.push({ value: toShopifySort(appSort), appSort, label, selected: false });
    });
    if (!keep.length) return;
    if (!keep.some((o) => o.selected)) keep[0].selected = true;

    native.innerHTML = "";
    fakeList.innerHTML = "";
    keep.forEach((o, i) => {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.label;
      opt.dataset.index = String(i);
      opt.dataset.appSort = o.appSort;
      if (o.selected) opt.selected = true;
      native.appendChild(opt);

      const div = document.createElement("div");
      div.className = "m-select-custom--option" + (o.selected ? " isActive" : "");
      div.dataset.value = o.value;
      div.dataset.appSort = o.appSort;
      div.textContent = o.label;
      fakeList.appendChild(div);
    });

    if (mobileList) {
      mobileList.innerHTML = "";
      keep.forEach((o, i) => {
        const li = document.createElement("li");
        li.className = "m-sortby-mobile--item" + (o.selected ? " active" : "");
        li.dataset.value = o.value;
        li.dataset.appSort = o.appSort;
        li.dataset.index = String(i);
        li.innerHTML = `<span>${o.label}</span>`;
        mobileList.appendChild(li);
      });
    }

    paintLabel(keep.find((o) => o.selected).label);
  };

  /** Keep every surface that shows the current sort in agreement. */
  const paintLabel = (label, appSort) => {
    const wrap = $(PROXY);
    if (wrap) {
      const text = $(".m-select-custom--trigger-text", wrap);
      if (text) text.textContent = label;
      $$(".m-select-custom--option", wrap).forEach((o) =>
        o.classList.toggle("isActive", appSort ? o.dataset.appSort === appSort : o.textContent.trim() === label)
      );
    }
    const mobileTrigger = $("[data-sortby-option]");
    if (mobileTrigger) mobileTrigger.textContent = label;
    $$(".m-sortby-mobile--item").forEach((li) =>
      li.classList.toggle("active", appSort ? li.dataset.appSort === appSort : li.textContent.trim() === label)
    );
  };

  const closeDropdowns = () => {
    const wrap = $(PROXY);
    if (wrap) $(".m-select-custom.js-selectCustom", wrap)?.classList.remove("isActive");
    window.MinimogTheme?.Collection?.closeMobileSorting?.();
  };

  /** Last resort when the filter app is not on the page: the theme's own sort path. */
  const nativeSort = (shopifyValue) => {
    if (!shopifyValue) return;
    const formSelect = $("[data-form-sorting] select");
    const form = document.getElementById("CollectionFiltersForm");
    if (formSelect && form) {
      formSelect.value = shopifyValue;
      form.dispatchEvent(new Event("input"));
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set("sort_by", shopifyValue);
    window.location.assign(url.toString());
  };

  const applySort = (appSort, shopifyValue, label) => {
    paintLabel(label, appSort);
    closeDropdowns();
    const li = appSort && $(`${APP_SORT_MENU} li[data-sort="${appSort}"]`);
    // The app's strip is display:none by design; a programmatic click still runs its handlers.
    if (li) li.click();
    else nativeSort(shopifyValue);
  };

  /**
   * Own the option clicks in the CAPTURE phase. `m-select-component` binds its handler directly to
   * each option node, so an ancestor capture listener is the only ordering-independent way to take
   * over — this script is deferred and would always lose a bubble-phase race. It also means options
   * this file appended behave identically to the ones Liquid rendered.
   */
  const wireDesktopProxy = (wrap) => {
    wrap.addEventListener(
      "click",
      (e) => {
        const opt = e.target.closest(".m-select-custom--option");
        if (!opt || !wrap.contains(opt)) return;
        e.preventDefault();
        e.stopPropagation();
        applySort(opt.dataset.appSort || toAppSort(opt.dataset.value, appSortMap()), opt.dataset.value, opt.textContent.trim());
      },
      true
    );
  };

  // The mobile sheet's list is delegated by storefont-filters.js — but only when it binds the
  // toolbar select, which the proxy rename prevents. So the proxy template needs its own handler.
  document.addEventListener(
    "click",
    (e) => {
      if (!$(PROXY)) return; // collection template: leave the theme's own delegate alone
      const li = e.target.closest(".m-sortby-mobile--item");
      if (!li) return;
      e.preventDefault();
      e.stopPropagation();
      applySort(li.dataset.appSort || toAppSort(li.dataset.value, appSortMap()), li.dataset.value, li.textContent.trim());
    },
    true
  );

  /** The app owns sorting after a forwarded click, so mirror its trigger rather than guessing. */
  let labelObserver;
  const watchAppLabel = () => {
    const trigger = $(`${APP_SORT_TRIGGER} span`);
    if (!trigger) return;
    labelObserver?.disconnect();
    labelObserver = new MutationObserver(() => {
      const label = trigger.textContent.trim();
      if (label) paintLabel(label);
    });
    labelObserver.observe(trigger, { childList: true, characterData: true, subtree: true });
  };

  /* ── reset pill ─────────────────────────────────────────────────────────── */

  const syncResetPill = () => {
    const box = $("[data-toolbar-reset]");
    const link = $("[data-toolbar-reset-link]");
    if (!box || !link) return;

    const src = new URL(window.location.href);
    const out = new URL(src.pathname, src.origin);
    let dropped = 0;
    src.searchParams.forEach((value, key) => {
      if (KEEP_PARAMS.has(key)) out.searchParams.append(key, value);
      else dropped += 1;
    });
    if (dropped > 0) link.setAttribute("href", `${out.pathname}${out.search}`);

    const serverApplied = box.dataset.serverApplied === "true";
    box.classList.toggle("m:hidden", dropped === 0 && !serverApplied);
  };

  /* ── result count, mirrored onto the sort line ──────────────────────────── */

  /**
   * The app's count element is a block that renders below the toolbar, so it reads as an orphaned
   * line under the sort control. Its text is mirrored into the toolbar slot instead of moving the
   * node: the app keeps updating its own element (its update logic is untouched), and the original
   * is hidden only once a mirror actually exists — so if any of this fails, today's behaviour stands.
   */
  const syncCount = () => {
    const slot = $("[data-toolbar-count]");
    if (!slot) return;
    const summary = $(APP_SUMMARY);
    const text = summary ? summary.textContent.replace(/\s+/g, " ").trim() : "";

    if (!text) {
      slot.hidden = true;
      document.documentElement.classList.remove(MIRRORED);
      return;
    }
    if (slot.textContent !== text) slot.textContent = text;
    slot.hidden = false;
    document.documentElement.classList.add(MIRRORED);
  };

  /* ── empty state, revealed from the rendered grid ───────────────────────── */

  /**
   * The filter app owns the grid on the search template and paints its own bare "no products"
   * line from its admin config — there is no Liquid to restyle. So the theme ships the polished
   * panel hidden (snippets/catalog-empty-state.liquid) and it is revealed here.
   *
   * The trigger is the rendered DOM, not a count: Shopify's search.results_count and the app's
   * summary disagree in both directions on this store, and a card either exists or it does not.
   * The app's own message is hidden only once ours is showing, so if any of this fails the
   * shopper still gets today's behaviour rather than a blank page.
   */
  const syncEmptyState = () => {
    const panel = $("[data-catalog-empty-managed]");
    if (!panel) return;
    const grid = gridNode();
    if (!grid) return;

    const hasCards = !!$(".m-product-item", grid);
    panel.classList.toggle("m:hidden", hasCards);

    // With no cards, whatever else the app left in the grid IS its placeholder message, so it is
    // suppressed while our panel stands in for it — and restored the moment results return.
    [...grid.children].forEach((node) => {
      const supersede = !hasCards && !node.matches(".m-product-item, [data-catalog-empty]");
      node.classList.toggle("mex-empty-superseded", supersede);
    });
  };

  let countObserver;
  const observeCount = () => {
    const summary = $(APP_SUMMARY);
    if (!summary) return;
    countObserver?.disconnect();
    countObserver = new MutationObserver(syncCount);
    countObserver.observe(summary, { childList: true, characterData: true, subtree: true });
  };

  /* ── survive the app's grid re-render ───────────────────────────────────── */

  let gridObserver;
  const observeGrid = () => {
    const grid = gridNode();
    if (!grid) return;
    gridObserver?.disconnect(); // never stack observers across re-inits
    gridObserver = new MutationObserver(() => {
      const collection = window.MinimogTheme?.Collection;
      collection?.toggleView?.(collection.activeCol);
      window.MinimogTheme?.Wishlist?.setWishlistButtonsState?.();
      window.MinimogTheme?.CompareProduct?.setCompareButtonsState?.();
      syncResetPill();
      syncEmptyState();
      // The app can replace its count element outright, which would kill the observer above it.
      observeCount();
      syncCount();
    });
    gridObserver.observe(grid, { childList: true });
  };

  /* ── boot ───────────────────────────────────────────────────────────────── */

  const boot = () => {
    const wrap = $(PROXY);
    // A section replaceWith yields a fresh node with no flag, which is exactly when we want to
    // re-wire; the flag only stops double-binding the same node.
    if (wrap && wrap.dataset.mexWired !== "1") {
      wrap.dataset.mexWired = "1";
      reconcileSortOptions(wrap);
      wireDesktopProxy(wrap);
      watchAppLabel();
    }
    syncResetPill();
    observeGrid();
    observeCount();
    syncCount();
    syncEmptyState();
  };

  /**
   * The Reset pill is driven by the query string, but the filter app writes the URL AFTER it swaps
   * the grid — so syncing on the grid mutation alone reads the previous URL and the pill stays
   * hidden. history.pushState/replaceState fire no event of their own, so they are wrapped here to
   * emit one. The originals are always called through; this adds a notification, nothing else.
   */
  const URL_CHANGED = "mex:urlchange";
  ["pushState", "replaceState"].forEach((method) => {
    const original = history[method];
    if (typeof original !== "function" || original.__mexWrapped) return;
    const wrapped = function (...args) {
      const result = original.apply(this, args);
      window.dispatchEvent(new Event(URL_CHANGED));
      return result;
    };
    wrapped.__mexWrapped = true;
    history[method] = wrapped;
  });

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  // The filter app boots after us and injects its sort menu late.
  window.addEventListener("load", boot);
  document.addEventListener("collection:rerendered", boot);
  const syncBar = () => {
    syncResetPill();
    syncCount();
  };
  window.addEventListener("popstate", syncBar);
  window.addEventListener(URL_CHANGED, syncBar);
})();
